import { api } from '../../../plugins/axios';
import { Alert } from '../../../plugins/alert';
import { Variants } from './variants';
import { Option } from './option';

export class Product {
	id = undefined;
	title = undefined;
	sku = undefined;
	variants = [];
	translations = [];
	category = undefined;
	manufacturer = undefined;
	tax_rate = undefined;
	is_exempt = undefined;
	is_digital = undefined;
	images = undefined;
	title_en = undefined;
	description_en = undefined;
	short_description_en = undefined;
	tags_en = undefined;
	title_ar = undefined;
	description_ar = undefined;
	short_description_ar = undefined;
	tags_ar = undefined;
	product_images = [];
	options = [];
	external_link = undefined;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
		this.translations.forEach((translation) => {
			const locale = translation.locale;
			Object.keys(translation).forEach((key) => {
				if (['id', 'product_id', 'locale'].includes(key)) return;
				this[`${key}_${locale}`] = translation[key];
			});
		});
		if (this.tags_en && typeof this.tags_en === 'string') {
			this.tags_en = this.tags_en?.split(', ');
		}
		if (this.tags_ar && typeof this.tags_ar === 'string') {
			this.tags_ar = this.tags_ar?.split(', ');
		}
		if (!this.tax_rate) {
			this.is_exempt = true;
		}
		this.variants = (this.variants || []).map((variant) => new Variants(variant));
		if (!this.options || !this.options.length) {
			this.options = [];
			this.variants.forEach((variant) => {
				(variant.option_values || []).forEach((optionValue) => {
					this.options.push(new Option(optionValue.option));
				});
			});
		}
		if (!this.product_images) this.product_images = [];

		if (!this.external_link) {
			this.external_link = this.variants?.[0]?.external_link || null;
		}
	}

	static async getList(params) {
		const res = await api.get('/products/index', { params });
		const data = res.data.result.products.data.map((product) => new Product(product));
		const totalRecords = res.data.result.products.meta?.total;

		return {
			data,
			totalRecords,
		};
	}

	static async getProductsWithVariants(params) {
		const res = await api.get('/products/ajax-products-with-variants', { params });
		const data = res.data.result.products.data.map((product) => new Product(product));
		const totalRecords = res.data.result.products.total;

		return {
			data,
			totalRecords,
		};
	}

	static autoLinkProducts(data) {
		return api.post('/products/bulk-link', data);
	}

	static async getLinkedProduct(productId) {
		const res = await api.get('/products/linked_product', {
			params: {
				product_id: productId,
			},
		});
		const marketplaces = res.data.result.marketplaces;
		const marketplacesList = res.data.result.marketplaces_list;
		const linkedMarketplaces = res.data.result.linked_marketplaces;

		return {
			marketplaces,
			marketplacesList,
			linkedMarketplaces,
		};
	}

	static async getCommonMarketplaces(product_ids) {
		const res = await api.post('/products/linked_product/common-marketplace', { product_ids });

		const marketplaces = res.data.marketplaces;
		return marketplaces;
	}

	static async bulkDetach(product_ids, marketplace_ids, data = {}) {
		try {
			await api.post('/products/linked_product/bulk-detach', {
				product_ids,
				marketplace_ids,
				...data,
			});
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async adjustTax(data) {
		try {
			await api.post('/products/tax_rate/bulk_add', data);
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async bulkAction(productIds, actionType, data = {}) {
		try {
			await api.post('/products/ajaxBulkActions', {
				ids: productIds,
				action_type: actionType,
				...data,
			});
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async bulkListing(productIds, marketplacesIds, data = {}) {
		try {
			await api.post('/products/linked_product/bulk-list', {
				ids: productIds,
				linked_marketplace_ids: marketplacesIds,
				...data,
			});
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async storeProduct(data) {
		try {
			const res = await api.post('/products/store', data);
			return new Product(res.data.result.product);
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async getById(id) {
		const res = await api.get(`/products/product-details/${id}`);
		const data = res.data.result.product;
		return new Product(data);
	}

	async update(data) {
		try {
			await api.post(`/products/update-product/${this.id}`, data);
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	async getLinkedMarketplaces() {
		const res = await api.get(`/products/linked_product?product_id=${this.id}`);
		return res.data.result.linked_marketplaces;
	}

	static addImage(data) {
		return api.post(`/products/product_images`, data);
	}

	static deleteImage(id, params = {}) {
		return api.delete(`/products/product_images/${id}`, { params });
	}

	static async checkSku(sku) {
		const res = await api.get('/products/validate-sku', { params: { sku } });
		return res.data;
	}

	static async updateLinkedProduct(productId, data) {
		try {
			const response = await api.put(`/products/linked_product/update-linked-product/${productId}`, data);
			
			const errors = response?.data?.result?.errors || response?.data?.errors;
			if (errors && Object.keys(errors).length > 0) {
				const errorMessages = [];
				Object.values(errors).forEach(marketplaceError => {
					errorMessages.push(`${marketplaceError.marketplace_name}: ${marketplaceError.errors.join(', ')}`);
				});
				Alert.error(errorMessages.join(' | '));
			}
			
			return response;
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async attachLinkedMarketplace(productId, linkedMarketplaceId) {
		try {
			await api.post(`/products/linked_product/attach-new-linked-marketplace/${productId}`, {
				linked_marketplaces: [linkedMarketplaceId],
			});
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}

	static async detachLinkedMarketplace(productId, linkedMarketplaceId) {
		try {
			await api.post(`/products/linked_product/detach-linked-marketplace/${productId}`, {
				linked_marketplaces: [linkedMarketplaceId],
			});
		} catch (err) {
			if (err?.response?.data?.message) {
				Alert.error(err?.response?.data?.message);
			}
			throw err;
		}
	}
}

export const productListingStatusEnum = Object.freeze({
	Listed: 'LISTED',
	UnListed: 'UNLISTED',
});

export const productActiveStatusEnum = Object.freeze({
	Active: 'Active',
	Disabled: 'Disabled',
});
